import { useCallback, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { HiChevronDown, HiQuestionMarkCircle } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import Tooltip from '#globalComponents/Tooltip/Tooltip';

import TagMultiSelect from '../../company/TagMultiSelect/TagMultiSelect';

const NONE_VALUE = '__none__';

const MODE_OPTIONS = [
	{ value: 'upsert', label: 'Anlegen + aktualisieren' },
	{ value: 'create', label: 'Nur neue anlegen' },
	{ value: 'update', label: 'Nur bestehende aktualisieren' },
];

const ENTITY_LABELS = { company: 'Firma', contact: 'Kontakt' };

// The keys the import recognises existing records by (company email only when it is not a freemail address).
const IDENTIFIER_FIELDS = ['contact.emailAddress', 'company.websiteUrl', 'company.emailAddress'];

// Virtual contact field: the CSV's "full name" column is split into firstName/lastName on
// import. It is editable like any other field (and can be unset to '— Nicht zuordnen —') so
// the auto-mapper's guess can be overridden — e.g. a "Name" column that is really a company name.
const FULL_NAME_FIELD = { key: '_fullName', label: 'Vollständiger Name (wird aufgeteilt)', entity: 'contact' };

export default function ColumnMappingStep({
	headers,
	sampleRows,
	availableTargetFields,
	columnMapping,
	onMappingChange,
	mode,
	onModeChange,
	fuzzyNameMatch = false,
	onFuzzyNameMatchChange,
	pipelines = [],
	pipelineId = '',
	stageId = '',
	onPipelineChange,
	onStageChange,
	allTags = [],
	importTags = [],
	onImportTagsChange,
	onCreateTag,
	contactLists = [],
	contactListId = '',
	onContactListChange,
	listName = '',
	onConfirm,
	onBack,
}) {
	const selectedPipeline = pipelines.find((p) => p.id === pipelineId) ?? null;
	const stages = selectedPipeline?.stages ?? [];
	const [localMapping, setLocalMapping] = useState(() => {
		const initial = {};
		for (const field of availableTargetFields ?? []) {
			initial[field.key] = columnMapping[field.key] ?? NONE_VALUE;
		}
		// _fullName is a virtual contact field (not in availableTargetFields). Seed it so its
		// dropdown is always present and editable, defaulting to the auto-mapper's guess.
		initial[FULL_NAME_FIELD.key] = columnMapping[FULL_NAME_FIELD.key] ?? NONE_VALUE;
		return initial;
	});

	const handleFieldChange = useCallback((targetField, csvColumn) => {
		setLocalMapping((prev) => ({ ...prev, [targetField]: csvColumn }));
	}, []);

	const handleConfirm = useCallback(() => {
		const clean = {};
		for (const [key, val] of Object.entries(localMapping)) {
			if (val && val !== NONE_VALUE) clean[key] = val;
		}
		onMappingChange(clean);
		onConfirm(clean);
	}, [localMapping, onMappingChange, onConfirm]);

	const companyFields = useMemo(
		() => (availableTargetFields ?? []).filter((f) => f.entity === 'company'),
		[availableTargetFields],
	);
	// _fullName leads the contact block so the name split is the first thing the user sees + can override.
	const contactFields = useMemo(
		() => [FULL_NAME_FIELD, ...(availableTargetFields ?? []).filter((f) => f.entity === 'contact')],
		[availableTargetFields],
	);

	// At least a contact identifier OR a company identifier must be mapped to import anything.
	const canImport = useMemo(() => {
		const mapped = (key) => localMapping[key] && localMapping[key] !== NONE_VALUE;
		const hasCompany = mapped('company.companyName') || mapped('company.websiteUrl');
		const hasContact =
			mapped('contact.firstName') ||
			mapped('contact.lastName') ||
			mapped('contact.emailAddress') ||
			mapped('_fullName');
		return hasCompany || hasContact;
	}, [localMapping]);

	const mappedColumns = useMemo(
		() => new Set(Object.values(localMapping).filter((val) => val && val !== NONE_VALUE)),
		[localMapping],
	);
	const ignoredColumns = useMemo(
		() => (headers ?? []).filter((h) => !mappedColumns.has(h)),
		[headers, mappedColumns],
	);
	const hasIdentifier = IDENTIFIER_FIELDS.some((key) => localMapping[key] && localMapping[key] !== NONE_VALUE);

	const firstRow = sampleRows?.[0] ?? {};

	const matchingOptions = (
		<>
			<div className="mb-4">
				<span className="text-sm font-medium text-text block mb-2">Importmodus</span>
				<div className="flex flex-wrap gap-2">
					{MODE_OPTIONS.map((opt) => (
						<RadioButton
							key={opt.value}
							name="importMode"
							value={opt.value}
							checked={mode === opt.value}
							onChange={onModeChange}
							label={opt.label}
						/>
					))}
				</div>
				<p className="text-xs text-bgBase-800 mt-1">
					Beim Aktualisieren werden nur leere Felder ergänzt — vorhandene Daten bleiben erhalten.
				</p>
			</div>

			{onFuzzyNameMatchChange && (
				<div className="mb-4">
					<Checkbox
						id="fuzzy-name-match"
						checked={fuzzyNameMatch}
						onChange={onFuzzyNameMatchChange}
						label={
							<span className="flex items-center gap-1.5">
								Unsicheres Firmen- und Kontaktmatching
								<Tooltip
									content={
										<div className="w-80 space-y-2 rounded-card bg-text px-3 py-2 text-xs text-white shadow-elevated">
											<p className="text-white">
												Normalerweise werden bestehende Einträge nur über eindeutige Merkmale
												erkannt: Firmen über ihre Website- oder E-Mail-Domain, Kontakte über
												ihre E-Mail-Adresse. Fehlen diese, wird die Zeile als neuer Eintrag
												angelegt.
											</p>
											<p className="text-white">
												Mit dieser Option wird zusätzlich über den Namen abgeglichen: Firmen
												über ihren Firmennamen und Kontakte ohne E-Mail über Vor- und Nachname
												innerhalb derselben Firma (Groß-/Kleinschreibung egal). Wird ein
												passender Eintrag gefunden, wird er aktualisiert statt neu angelegt — z.
												B. um eine fehlende Telefonnummer zu ergänzen.
											</p>
											<p className="text-white">
												Achtung: Namensabgleich ist ungenau. Gleich benannte Firmen oder zwei
												Personen mit gleichem Namen in derselben Firma können fälschlich
												zusammengeführt werden.
											</p>
										</div>
									}
								>
									<button
										type="button"
										className="text-bgBase-800 hover:text-text"
										aria-label="Wie funktioniert das unsichere Firmen- und Kontaktmatching?"
									>
										<HiQuestionMarkCircle className="h-4 w-4 cursor-help" />
									</button>
								</Tooltip>
							</span>
						}
					/>
				</div>
			)}
		</>
	);

	const mappingSection = (
		<>
			<div className="border border-bgBase-500 rounded-card overflow-hidden max-h-[350px] overflow-y-auto">
				<table className="w-full text-sm">
					<thead className="bg-bgBase-300 sticky top-0">
						<tr>
							<th className="text-left px-4 py-2 font-medium text-text">Zielfeld</th>
							<th className="text-left px-4 py-2 font-medium text-text">CSV-Spalte</th>
							<th className="text-left px-4 py-2 font-medium text-text">Vorschau</th>
						</tr>
					</thead>
					<tbody>
						<SectionRow label={ENTITY_LABELS.company} />
						{companyFields.map((field) => (
							<FieldRow
								key={field.key}
								field={field}
								value={localMapping[field.key] ?? NONE_VALUE}
								headers={headers}
								previewValue={previewFor(localMapping[field.key], firstRow)}
								onChange={handleFieldChange}
							/>
						))}

						<SectionRow label={ENTITY_LABELS.contact} />
						{contactFields.map((field) => (
							<FieldRow
								key={field.key}
								field={field}
								value={localMapping[field.key] ?? NONE_VALUE}
								headers={headers}
								previewValue={previewFor(localMapping[field.key], firstRow)}
								onChange={handleFieldChange}
							/>
						))}
					</tbody>
				</table>
			</div>

			<IgnoredColumnsHint columns={ignoredColumns} />

			{canImport && !hasIdentifier && <MissingIdentifierWarning />}
		</>
	);

	return (
		<div>
			<p className="text-sm text-text mb-4">
				{listName
					? `Jede Zeile wird in die Liste „${listName}“ aufgenommen: mit Ansprechpartner als Kontakt, ohne als Firma. Laufende Kampagnen der Liste planen neue Empfänger sofort ein.`
					: 'Ordnen Sie die CSV-Spalten den CRM-Feldern zu. Nicht zugeordnete Spalten werden ignoriert.'}
			</p>

			{listName ? (
				<>
					{mappingSection}
					<CollapsibleSection title="Weitere Optionen für die CRM-Daten">
						{matchingOptions}
					</CollapsibleSection>
				</>
			) : (
				<>
					{matchingOptions}
					{onContactListChange && (
						<ContactListSection lists={contactLists} value={contactListId} onChange={onContactListChange} />
					)}

					{pipelines.length > 0 && (
						<div className="mb-4 p-3 bg-bgBase-300 border border-bgBase-500 rounded-card">
							<span className="text-sm font-medium text-text block mb-2">
								Firmen einer Pipeline zuweisen{' '}
								<span className="text-bgBase-800 font-normal">(optional)</span>
							</span>
							<div className="flex flex-wrap items-center gap-2">
								<Select
									value={pipelineId}
									onChange={(e) => {
										const id = e.target.value;
										onPipelineChange(id);
										const first = pipelines.find((p) => p.id === id)?.stages?.[0];
										onStageChange(first?.id ?? '');
									}}
									className="min-w-[220px]"
								>
									<option value="">— Keine Pipeline —</option>
									{pipelines.map((p) => (
										<option key={p.id} value={p.id}>
											{p.name}
										</option>
									))}
								</Select>
								{pipelineId && (
									<Select
										value={stageId}
										onChange={(e) => onStageChange(e.target.value)}
										className="min-w-[180px]"
									>
										{stages.map((s) => (
											<option key={s.id} value={s.id}>
												{s.name}
											</option>
										))}
									</Select>
								)}
							</div>
							<p className="text-xs text-bgBase-800 mt-1">
								Für jede importierte Firma wird ein Deal in dieser Pipeline/Phase angelegt. Firmen mit
								bereits vorhandenem Deal in dieser Pipeline werden übersprungen.
							</p>
						</div>
					)}

					{onImportTagsChange && (
						<div className="mb-4 p-3 bg-bgBase-300 border border-bgBase-500 rounded-card">
							<span className="text-sm font-medium text-text block mb-2">
								Tags vergeben <span className="text-bgBase-800 font-normal">(optional)</span>
							</span>
							<TagMultiSelect
								value={importTags}
								allTags={allTags}
								onChange={onImportTagsChange}
								onCreateTag={onCreateTag}
								placeholder="Tag auswählen oder erstellen …"
							/>
							<p className="text-xs text-bgBase-800 mt-1">
								Die gewählten Tags werden allen importierten Firmen zugewiesen.
							</p>
						</div>
					)}

					{mappingSection}
				</>
			)}

			{!canImport && (
				<p className="text-sm text-caution-700 mt-3">
					Ordnen Sie mindestens einen Firmen- (Name oder Website) und einen Kontaktwert zu.
				</p>
			)}

			<div className="flex justify-between mt-6">
				<Button variant="outline" onClick={onBack}>
					Zurück
				</Button>
				<Button onClick={handleConfirm} disabled={!canImport}>
					Import starten
				</Button>
			</div>
		</div>
	);
}

function ContactListSection({ lists, value, onChange }) {
	return (
		<div className="mb-4 p-3 bg-bgBase-300 border border-bgBase-500 rounded-card">
			<span className="text-sm font-medium text-text block mb-2">
				In Kontaktliste aufnehmen <span className="text-bgBase-800 font-normal">(optional)</span>
			</span>
			<Select value={value} onChange={(e) => onChange(e.target.value)} className="min-w-[220px]">
				<option value="">— Keine Kontaktliste —</option>
				{lists.map((list) => (
					<option key={list.id} value={list.id}>
						{list.name}
					</option>
				))}
			</Select>
			<p className="text-xs text-bgBase-800 mt-1">
				Jede Zeile mit Ansprechpartner kommt als Kontakt in die Liste, jede Zeile ohne Ansprechpartner als
				Firma. Laufende Kampagnen dieser Liste planen die neuen Empfänger sofort ein.
			</p>
		</div>
	);
}

function CollapsibleSection({ title, children }) {
	const [isOpen, setIsOpen] = useState(false);
	return (
		<div className="mt-4 border border-bgBase-500 rounded-card">
			<button
				type="button"
				onClick={() => setIsOpen((open) => !open)}
				aria-expanded={isOpen}
				className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-text"
			>
				{title}
				<HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
			</button>
			{isOpen && <div className="px-3 pb-1">{children}</div>}
		</div>
	);
}

function IgnoredColumnsHint({ columns }) {
	if (columns.length === 0) return null;
	return (
		<div className="mt-3 text-sm text-bgBase-800">
			<span className="font-medium text-text">Nicht importierte Spalten ({columns.length}):</span>{' '}
			{columns.join(', ')}
		</div>
	);
}

function MissingIdentifierWarning() {
	return (
		<div className="mt-3 p-3 bg-caution-200 border border-caution-500/40 rounded-card">
			<p className="text-sm text-caution-700">
				Weder E-Mail noch Website ist zugeordnet. Ohne diese Spalten erkennt der Import keine bestehenden
				Kontakte und Firmen und legt jede Zeile neu an, auch wenn sie im CRM schon existiert.
			</p>
		</div>
	);
}

function previewFor(column, firstRow) {
	return column && column !== NONE_VALUE ? (firstRow[column] ?? '') : '';
}

function SectionRow({ label }) {
	return (
		<tr className="border-t border-bgBase-400">
			<td
				colSpan={3}
				className="px-4 py-2 bg-bgBase-300 text-xs font-semibold text-bgBase-800 uppercase tracking-wider"
			>
				{label}
			</td>
		</tr>
	);
}

function FieldRow({ field, value, headers, previewValue, onChange }) {
	return (
		<tr className="border-t border-bgBase-400">
			<td className="px-4 py-2">
				<span className="text-text">{field.label}</span>
				{field.isCustom && <span className="text-xs text-bgBase-800 ml-1">(eigenes Feld)</span>}
			</td>
			<td className="px-4 py-2">
				<Select value={value} onChange={(e) => onChange(field.key, e.target.value)}>
					<option value={NONE_VALUE}>— Nicht zuordnen —</option>
					{(headers ?? []).map((h) => (
						<option key={h} value={h}>
							{h}
						</option>
					))}
				</Select>
			</td>
			<td className="px-4 py-2 text-bgBase-800 truncate max-w-[200px]">{previewValue}</td>
		</tr>
	);
}

ColumnMappingStep.propTypes = {
	headers: PropTypes.arrayOf(PropTypes.string),
	sampleRows: PropTypes.arrayOf(PropTypes.object),
	availableTargetFields: PropTypes.arrayOf(PropTypes.object),
	columnMapping: PropTypes.object.isRequired,
	onMappingChange: PropTypes.func.isRequired,
	mode: PropTypes.string.isRequired,
	onModeChange: PropTypes.func.isRequired,
	fuzzyNameMatch: PropTypes.bool,
	onFuzzyNameMatchChange: PropTypes.func,
	pipelines: PropTypes.arrayOf(PropTypes.object),
	pipelineId: PropTypes.string,
	stageId: PropTypes.string,
	onPipelineChange: PropTypes.func,
	onStageChange: PropTypes.func,
	allTags: PropTypes.array,
	importTags: PropTypes.array,
	onImportTagsChange: PropTypes.func,
	onCreateTag: PropTypes.func,
	contactLists: PropTypes.arrayOf(PropTypes.object),
	contactListId: PropTypes.string,
	onContactListChange: PropTypes.func,
	listName: PropTypes.string,
	onConfirm: PropTypes.func.isRequired,
	onBack: PropTypes.func.isRequired,
};

ContactListSection.propTypes = {
	lists: PropTypes.arrayOf(PropTypes.object).isRequired,
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
};

CollapsibleSection.propTypes = {
	title: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
};

IgnoredColumnsHint.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SectionRow.propTypes = {
	label: PropTypes.string.isRequired,
};

FieldRow.propTypes = {
	field: PropTypes.object.isRequired,
	value: PropTypes.string.isRequired,
	headers: PropTypes.arrayOf(PropTypes.string),
	previewValue: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};
